/**
 * Ask for a password reset link. The server answers the same way whether or
 * not the email exists, and this page reflects that honestly.
 */
import { useState, type FormEvent } from 'react';
import { Send } from 'lucide-react';
import { api } from '../lib/api.ts';
import { Field, PandaMark, Spinner } from '../components/Bits.tsx';
import { OutcomeModal } from '../components/Overlay.tsx';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<null | { ok: boolean; message: string }>(null);
  const [fieldError, setFieldError] = useState<string | undefined>();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.includes('@')) {
      setFieldError('Enter the email you sign in with.');
      return;
    }
    setFieldError(undefined);
    setBusy(true);
    try {
      await api.requestReset(email.trim());
      setOutcome({ ok: true, message: 'If that email has an account, a reset link is on its way. It works once and expires in one hour.' });
    } catch {
      setOutcome({ ok: false, message: 'Could not reach the server. Check your connection and try again.' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-5 py-10">
      <div className="w-full" style={{ maxWidth: 400 }}>
        <div className="mb-7 flex flex-col items-center text-center">
          <PandaMark size={58} />
          <h1 className="mt-4 text-[22px] font-extrabold tracking-tight text-ink">Reset your password</h1>
          <p className="mt-1.5 text-[13.5px] leading-relaxed text-muted">
            Tell us your email and we will send a link to choose a new one.
          </p>
        </div>
        <form onSubmit={onSubmit} className="card card-lift p-6" noValidate>
          <Field label="Email" error={fieldError} id="forgot-email">
            <input
              id="forgot-email"
              className="field"
              type="email"
              autoComplete="email"
              value={email}
              aria-invalid={fieldError ? 'true' : undefined}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </Field>
          <button type="submit" className="btn btn-primary btn-lg w-full" disabled={busy}>
            {busy ? <Spinner size={16} label="Sending" /> : <Send size={16} aria-hidden="true" />}
            Email me a reset link
          </button>
          <p className="mt-4 text-center text-[13px]">
            <a href="/" className="font-semibold text-accent underline-offset-2 hover:underline">Back to sign in</a>
          </p>
        </form>
      </div>
      <OutcomeModal
        open={outcome !== null}
        onClose={() => setOutcome(null)}
        ok={outcome?.ok ?? true}
        title={outcome?.ok ? 'Check your email' : 'Could not finish'}
        message={outcome?.message ?? ''}
      />
    </div>
  );
}
